import { useLazyQuery } from '@apollo/client';
import type { AssetClassBranch } from '@interfaces/financialProduct';
import { useEffect, useMemo } from 'react';
import { FETCH_CASH_FINANCIAL_PRODUCT } from '../queries';
import { hasCashAssetClassBranch } from './useAssetClassGrouping';

/**
 * Fetches the org-level cash financial product (cache-first) for all strategies.
 *
 * - ASSET_CLASS: `hasCashBranch` gates the "+ Add Cash" button; use
 *   `(hasCashBranch || cashLoading)` to keep it visible during loading.
 * - STANDARD: `cashProduct` is consumed by `normalizeCashSecurity` (OVT-6062).
 */
export const useModelPortfolioCash = (
  assetClassBranches: AssetClassBranch[],
  assetClassLevel: string | undefined,
) => {
  const [
    fetchCashFinancialProduct,
    { loading: cashLoading, data: cashData, error: cashError },
  ] = useLazyQuery(FETCH_CASH_FINANCIAL_PRODUCT, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    fetchCashFinancialProduct();
  }, [fetchCashFinancialProduct]);

  const hasCashBranch = useMemo(
    () =>
      hasCashAssetClassBranch(
        cashData?.fetchCashFinancialProduct?.financialProduct,
        assetClassBranches,
        assetClassLevel,
      ),
    [cashData, assetClassBranches, assetClassLevel],
  );

  const cashProduct = cashData?.fetchCashFinancialProduct?.financialProduct;

  return { hasCashBranch, cashLoading, cashProduct, cashError };
};
